'use client'

import { useContext, useId, useState } from 'react'

import Button from '/react4xp/components/prototype/Button'
import Form, { FormContext } from '/react4xp/components/prototype/Form/Form'
import Input from '/react4xp/components/prototype/Form/Input'
import Textarea from '/react4xp/components/prototype/Form/Textarea'
import Checkbox from '/react4xp/components/prototype/Form/Checkbox'

import type { ContactFormData, ContactFormProps } from './types'

// The consent label is assembled as an HTML string (see Checkbox), so a
// caller-supplied URL has to be escaped before it lands in the href attribute.
function escapeAttr(value: string) {
	return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
}

// The submit row lives inside <Form>, because clearing the entered values means
// reaching react-hook-form through FormContext — which Form provides, so ContactForm
// itself (Form's parent) cannot read it.
function SubmitRow({
	isSent,
	recaptchaVerified,
	recaptchaRequiredMessage,
	sendButtonLabel,
	sentButtonLabel,
	newMessageButtonLabel,
	onNewMessage,
}: {
	isSent: boolean
	recaptchaVerified: boolean
	recaptchaRequiredMessage: string
	sendButtonLabel: string
	sentButtonLabel: string
	newMessageButtonLabel: string
	onNewMessage: () => void
}) {
	const formContext = useContext(FormContext)

	function handleNewMessage() {
		// Wipes the values and the validation state, so the fresh form starts pristine
		// rather than showing the previous message with every field already touched.
		formContext?.form.reset()
		onNewMessage()
	}

	return (
		<div className="flex flex-wrap items-center gap-3">
			<Button
				variant="primary"
				size="sm"
				type="submit"
				className="self-start"
				disabled={isSent || !recaptchaVerified}
				title={!recaptchaVerified ? recaptchaRequiredMessage : undefined}
			>
				{isSent ? sentButtonLabel : sendButtonLabel}
			</Button>

			{isSent && (
				<Button variant="secondary" size="sm" type="button" className="self-start" onClick={handleNewMessage}>
					{newMessageButtonLabel}
				</Button>
			)}
		</div>
	)
}

export default function ContactForm({
	heading,
	privacyLink,
	disclaimerMessage,
	labels = {},
	onSubmit,
	recaptchaVerified = false,
	recaptchaSlot,
	onReset,
	formSlot,
}: ContactFormProps) {
	const formId = useId()
	const nameId = `${formId}-name`
	const phoneId = `${formId}-phone`
	const emailId = `${formId}-email`
	const messageId = `${formId}-message`
	const consentId = `${formId}-consent`

	const {
		nameLabel = 'Navn',
		namePlaceholder = 'Navn',
		phoneLabel = 'Telefon',
		phonePlaceholder = 'Telefon',
		emailLabel = 'E-post',
		emailPlaceholder = 'E-post',
		messageLabel = 'Melding',
		messagePlaceholder = 'Melding',
		consentPrefix = 'Jeg har lest og godtar',
		consentLinkLabel = 'personvernerklæringen',
		nameRequiredMessage = 'Navn er påkrevd',
		emailRequiredMessage = 'E-post er påkrevd',
		invalidEmailMessage = 'Ugyldig e-postadresse',
		messageRequiredMessage = 'Melding er påkrevd',
		consentRequiredMessage = 'Du må godta personvernerklæringen',
		recaptchaRequiredMessage = 'Bekreft at du ikke er en robot',
		sendButtonLabel = 'Send inn',
		sentButtonLabel = 'Sendt',
		newMessageButtonLabel = 'Ny melding',
		genericErrorMessage = 'Noe gikk feil. Vennligst prøv igjen.',
	} = labels

	const [isSent, setIsSent] = useState(false)
	const [submitError, setSubmitError] = useState('')

	const linkHtml = privacyLink?.url
		? `<a href="${escapeAttr(privacyLink.url)}"${
				privacyLink.isExternal ? ' target="_blank" rel="noopener noreferrer"' : ''
			}>${consentLinkLabel}</a>`
		: consentLinkLabel
	const consentLabel = `${consentPrefix} ${linkHtml}<span aria-hidden="true" class="form-label-required">*</span>`

	async function handleFormSubmit(data: Record<string, unknown>) {
		if (!recaptchaVerified) return

		setSubmitError('')

		const formData: ContactFormData = {
			name: (data.name as string) ?? '',
			phone: data.phone as string | undefined,
			email: (data.email as string) ?? '',
			message: data.message as string | undefined,
			consent: data.consent === true || data.consent === 'true',
			recaptchaVerified,
		}

		try {
			await onSubmit?.(formData)
			setIsSent(true)
		} catch {
			// `recaptchaVerified` is the parent's to reset — it owns the widget.
			setSubmitError(genericErrorMessage)
		}
	}

	function handleNewMessage() {
		setIsSent(false)
		setSubmitError('')
		onReset?.()
	}

	return (
		<section className="my-12">
			<div className="container">
				<div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between md:gap-0">
					<div className="min-w-0 flex-1">
						{heading && <h2 className="h2 text-black-950">{heading}</h2>}
					</div>

					<div className="max-w-175 flex-1">
						{/* An external form owns its own markup, so everything below is skipped —
						    but the column it sits in stays the same. */}
						{formSlot ?? (
							<Form onSubmit={handleFormSubmit} className="flex flex-col gap-3">
								<div className="grid grid-cols-2 gap-6">
									<Input
										id={nameId}
										name="name"
										label={nameLabel}
										placeholder={namePlaceholder}
										type="text"
										required
										requiredMessage={nameRequiredMessage}
										disabled={isSent}
									/>
									<Input
										id={phoneId}
										name="phone"
										label={phoneLabel}
										placeholder={phonePlaceholder}
										type="text"
										disabled={isSent}
									/>
								</div>

								<Input
									id={emailId}
									name="email"
									label={emailLabel}
									placeholder={emailPlaceholder}
									type="email"
									required
									requiredMessage={emailRequiredMessage}
									invalidEmailMessage={invalidEmailMessage}
									disabled={isSent}
								/>

								<Textarea
									id={messageId}
									name="message"
									label={messageLabel}
									placeholder={messagePlaceholder}
									required
									requiredMessage={messageRequiredMessage}
									disabled={isSent}
								/>

								<Checkbox
									id={consentId}
									name="consent"
									label={consentLabel}
									required
									requiredMessage={consentRequiredMessage}
									disabled={isSent}
								/>

								{disclaimerMessage && (
									<div className="text-sm leading-[1.4] text-black-950 [&_a]:underline [&_a]:decoration-brand-500 [&_a]:underline-offset-3 [&_a]:decoration-[length:7%] [&_a]:[text-decoration-skip-ink:none]">
										{disclaimerMessage}
									</div>
								)}

								{recaptchaSlot !== null && (
									<div className="h-18.5 w-[18.8125rem] shrink-0 overflow-hidden rounded-lg bg-black-50">
										{recaptchaSlot}
									</div>
								)}

								{submitError && <p role="alert" className="text-sm leading-[1.4] text-red">{submitError}</p>}

								<SubmitRow
									isSent={isSent}
									recaptchaVerified={recaptchaVerified}
									recaptchaRequiredMessage={recaptchaRequiredMessage}
									sendButtonLabel={sendButtonLabel}
									sentButtonLabel={sentButtonLabel}
									newMessageButtonLabel={newMessageButtonLabel}
									onNewMessage={handleNewMessage}
								/>
							</Form>
						)}
					</div>
				</div>
			</div>
		</section>
	)
}
